import { CommonResponse, HOME_GET, MINI_PROGRAM_GET_VERSION, netClient, HOME_COUPON_DOCKS } from '@/apis/api'
import type { VisitHistoryDTO } from '../history/history'
import type { AnniversaryGroupDTO, LifeMinDTO } from '../schedule/schedule'
import { ref } from '@vue/reactivity'
import { useLocalStorage } from '@vueuse/core'
import {
  CACHE_KEY_SOURCE,
  CACHE_KEY_ADD_TO_MINIPROGRAM,
  CACHE_KEY_SHOW_SUBSCRIBE_OFFICIAL_SITE,
  COUPON_DOCKS,
  CACHE_KEY_SHOW_COUPON
} from '@/constants'
import type { CouponDTO, CouponUsageDTO } from '../member/member'
import type { UserMinDTO } from '../user/users'
import { handleErr } from '@/composables/exception/handler'
import { CODE_OK } from '@/apis/code'
import type { FileDTO } from '@/composables/media/media'
import { type MultiLang } from '@/composables/memorize/memorize'
import type { MemorialGiftDTO } from '@/composables/memorize/memorize'

import { ElMessage } from 'element-plus'
import { renderMessage } from '@/utils/render'

import { type Ref } from 'vue'
import type { AltarDTO, LifeDTO } from '@/composables/life/life'

export interface GetHomeResponse {
  recentAnniversaries: Array<AnniversaryGroupDTO>
  lives: Array<LifeMinDTO>
  participatedAltars: Array<AltarDTO> // 添加了
  totalLifeCount: number
  stat: SiteStatMessage
  isMeSubscribed: boolean
  obituaryThisWeek: number
  top: HomeTop
  favoriteLives: Array<LifeDTO> // 近期纪念馆
  openFigureLives: Array<LifeDTO> // 公众人物纪念馆
  todayBirthdayFigureLives: Array<LifeDTO> // 今日生日人物纪念馆
  famousFigures: Array<FamousFigure>
  banners: Array<HomeBannerDTO>
  coupons: Array<CouponDTO>
  couponUsages: Array<CouponUsageDTO>
  articles: Array<ArticleDTO> // 添加articles字段，用于文章模块
}

// 顶部的
export interface HomeTop {
  logoUrl: string // logo的url
  slogan: string // logo后面的文字
  startColor: string // 顶部渐变的起始颜色
  endColor: string // 顶部渐变的末尾颜色
  imgUrl: string // 顶部的装饰图，左下角对齐
  pcImgUrl: string // 添加PC端顶部背景图 - md
}

// 著名人物纪念配置
export interface FamousFigure {
  id: string
  title: string // 标题
  avatarUrl: string // 头像
  passDate: number // 去世时间 - 展示在第二行，于yyyy/mm/dd逝世
  actionText: string // 动作文字 - 如：查看他的生平共同缅怀
  actionUrl: string // 动作链接 - 如：杨振宁的纪念网站url
  createdAt: number // 创建时间
  updatedAt: number
}

export interface SiteStatMessage {
  todayGift: number
  todayLife: number
  todayCommentCount: number
  todayPhoto: number
  todayWorship: number
}

export interface GetMiniProgramVersionResponse {
  auditVersionCode: number
}

export const couponDocks = useLocalStorage<CouponDockDTO[]>(COUPON_DOCKS, [])

export async function retriveCouponDocks() {
  const { data, error, statusCode } = await getHomeCouponDocks()
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as unknown as Ref<CommonResponse<CouponDocksResponse>>
    if (resData.value.code === CODE_OK) {
      const response = data.value?.data as CouponDocksResponse
      couponDocks.value = response.docks
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
    }
  }
}

export function getHome() {
  return netClient.request<CommonResponse<GetHomeResponse>>(HOME_GET, 'get', {})
}
export interface CouponDockDTO {
  id: string
  dockName: string
  coupon: CouponDTO
  creator: UserMinDTO
  updater: UserMinDTO
  createdAt: number
  updatedAt: number
  deletedAt: number
}

export interface CouponDocksResponse {
  docks: Array<CouponDockDTO>
}

export function getHomeCouponDocks() {
  return netClient.request<CommonResponse<CouponDocksResponse>>(HOME_COUPON_DOCKS, 'get', {})
}

export function getMiniProgramVersion() {
  return netClient.request<CommonResponse<GetMiniProgramVersionResponse>>(MINI_PROGRAM_GET_VERSION, 'get', {})
}

const _addToMiniprogramCloseOnce = useLocalStorage<boolean>(CACHE_KEY_ADD_TO_MINIPROGRAM, false) // 缓存是否显示过添加到小程序的提醒
export const addToMiniprogramCloseOnce = _addToMiniprogramCloseOnce

const _showSubscribeOfficialSite = useLocalStorage<boolean>(CACHE_KEY_SHOW_SUBSCRIBE_OFFICIAL_SITE, true)
export const showSubscribeOfficialSite = _showSubscribeOfficialSite

export const refreshpage = useLocalStorage('refreshpage', new Date().getTime())

//banner部分

export interface HomeBannerDTO {
  id: string
  bannerType: number // 首页Banner类型 1 - 礼包 2 - 小祭台 3 - 会员优惠券 4 - 文章
  style: string // 'image_txt' 图文, 'image' 图片
  bgColorStart: string // 背景渐变开始颜色 45度渐变
  bgColorEnd: string // 背景渐变结束颜色
  leftImage: string // 左侧的图片 - 复用于会员弹窗的图片
  bgImage: string // 背景图片，当style='image' 时有用。
  txtTitle: string // 标题
  txtSubtitle: string // 子标题
  txtColor: string // 标题颜色
  actionTxt: string // 操作按钮文字
  actionBgColor: string // 操作按钮背景色
  actionTxtColor: string // 操作按钮文字色
  outdated: boolean // 是否过期
  clickTimes: number // 点击次数
  payload: AltarBannerPayload | GiftBannerPayload | MemberCouponBannerPayload | ArticleBannerPayload // 负载
  operatorId: string
  rank: number // 排序
  createdAt: number
  updatedAt: number
}

export interface HomeBannerPayload {
  type: number
}

// 小祭台-祭品
export interface AltarBannerPayload extends HomeBannerPayload {
  offeringId: string
  theme: string
}

// 礼物
export interface GiftBannerPayload extends HomeBannerPayload {
  giftId: string
  giftCategory: string
}

// 会员领取优惠券 -
export interface MemberCouponBannerPayload extends HomeBannerPayload {
  couponId: string
}

export interface ArticleBannerPayload {
  type: number
  articleId: string
  url: string
}

export interface CouponCloseOnce {
  couponId: string
  expiredAt: number
}

// Persisted list of coupon-close records; default to empty array
export const showCoupon = useLocalStorage<CouponCloseOnce[]>(CACHE_KEY_SHOW_COUPON, [])

export function setCouponExpired(couponId: string) {
  // Ensure storage is an array; if not, reset to []
  if (!Array.isArray(showCoupon.value)) {
    showCoupon.value = []
  }
  const coupon = showCoupon.value.find((it) => it.couponId === couponId)
  if (coupon) {
    coupon.expiredAt = Date.now() + 1000 * 60 * 60 * 24
  } else {
    showCoupon.value.push({
      couponId,
      expiredAt: Date.now() + 1000 * 60 * 60 * 24
    })
  }
}

export function isCouponShow(couponId: string) {
  const now = Date.now()
  // Ensure storage is an array; if not, reset to [] to avoid runtime errors
  if (!Array.isArray(showCoupon.value)) {
    showCoupon.value = []
  }
  // 清理所有已过期的记录（过期时间早于或等于当前时间）
  showCoupon.value = showCoupon.value.filter((it) => it.expiredAt > now)
  // 是否存在仍有效的记录：存在则未过期，不展示；不存在则视为过期或未设置，展示
  const exists = showCoupon.value.some((it) => it.couponId === couponId)
  return !exists
}

export interface Ad {
  img: string
  type: string
  id: any
  data: any
}
